import React from 'react';

interface FieldProps {
  label: string;
  htmlFor?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  className?: string;
  children: React.ReactNode;
}

export const Field: React.FC<FieldProps> = ({
  label,
  htmlFor,
  hint,
  error,
  required,
  className = '',
  children,
}) => (
  <div className={['space-y-1.5', className].join(' ')}>
    <label
      htmlFor={htmlFor}
      className="text-xs font-medium text-sand-700 tracking-wide uppercase"
    >
      {label}
      {required && <span className="text-terracotta-500 ml-0.5">*</span>}
    </label>
    {children}
    {error ? (
      <p className="text-xs text-red-600">{error}</p>
    ) : hint ? (
      <p className="text-xs text-sand-500">{hint}</p>
    ) : null}
  </div>
);

const baseInput =
  'w-full bg-white border border-sand-200 rounded-xl px-3.5 h-10 text-sm text-sand-900 placeholder-sand-400 ' +
  'transition-all focus:outline-none focus:border-sage-500 focus:ring-2 focus:ring-sage-500/20 ' +
  'disabled:bg-sand-50 disabled:text-sand-500 disabled:cursor-not-allowed';

export const Input: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = ({
  className = '',
  ...rest
}) => (
  <input {...rest} className={[baseInput, className].join(' ')} />
);

export const Textarea: React.FC<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>
> = ({ className = '', rows = 3, ...rest }) => (
  <textarea
    {...rest}
    rows={rows}
    className={[
      'w-full bg-white border border-sand-200 rounded-xl px-3.5 py-2.5 text-sm text-sand-900 placeholder-sand-400 ' +
        'transition-all focus:outline-none focus:border-sage-500 focus:ring-2 focus:ring-sage-500/20 ' +
        'disabled:bg-sand-50 disabled:text-sand-500',
      className,
    ].join(' ')}
  />
);

export const Select: React.FC<React.SelectHTMLAttributes<HTMLSelectElement>> = ({
  className = '',
  children,
  ...rest
}) => (
  <div className="relative">
    <select
      {...rest}
      className={[
        baseInput,
        'pr-9 appearance-none bg-no-repeat',
        className,
      ].join(' ')}
    >
      {children}
    </select>
    <svg
      className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-sand-500"
      viewBox="0 0 20 20"
      fill="currentColor"
    >
      <path
        fillRule="evenodd"
        d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 011.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"
        clipRule="evenodd"
      />
    </svg>
  </div>
);

export const Checkbox: React.FC<{
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  hint?: string;
}> = ({ label, checked, onChange, hint }) => (
  <label className="inline-flex items-start gap-2.5 cursor-pointer select-none group">
    <span
      className={[
        'mt-0.5 flex h-5 w-5 items-center justify-center rounded-md border transition-colors',
        checked
          ? 'bg-sage-600 border-sage-600'
          : 'bg-white border-sand-300 group-hover:border-sand-400',
      ].join(' ')}
    >
      {checked && (
        <svg className="h-3 w-3 text-white" viewBox="0 0 12 12" fill="none">
          <path
            d="M2 6l3 3 5-6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </span>
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="sr-only"
    />
    <span className="text-sm">
      <span className="text-sand-900 font-medium">{label}</span>
      {hint && <span className="block text-xs text-sand-500 mt-0.5">{hint}</span>}
    </span>
  </label>
);
